import { useState, useEffect } from "react";
import React from "react";
import { Plus, AlertTriangle, AlertCircle, Settings, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { ProjectsList } from "@/components/projects/ProjectsList";
import { ProjectDetailsTabs } from "@/components/projects/ProjectDetailsTabs";
import { ProjectForm } from "@/components/projects/ProjectForm";

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [isEditingInline, setIsEditingInline] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Force refresh projects data on mount to get latest from database
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["projects"] });
  }, [queryClient]);

  // Role-based access control
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>You must be logged in to access this page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Fetch projects from Supabase
  const { data: projects, isLoading, error, refetch } = useQuery({
    queryKey: ["projects", user?.id],
    queryFn: async () => {
      console.log("DEBUG: Fetching projects data...");
      
      // Query all projects directly - no org_id filtering needed
      const { data, error } = await supabase
        .from("projects")
        .select(`
          id,
          name,
          status,
          start_date,
          delivered_date,
          promised_delivery_date,
          confirm_date,
          client_id,
          proposal_id,
          proposal_ids,
          value,
          sold_for,
          arr,
          tools,
          tool_prices,
          payment_schedule,
          price_comments,
          initial_cost_estimation,
          actual_costs,
          created_at,
          updated_at,
          clients(name, industry),
          proposals(title, status)
        `)
        .order('confirm_date', { ascending: false, nullsFirst: false });
      
      if (error) {
        console.error("DEBUG Projects: Query error", error);
        throw error;
      }
      
      console.log("DEBUG Projects: Projects found:", data?.length || 0);
      console.log("DEBUG Projects: Sample data:", data?.[0]);
      
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Fetch clients for form
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch proposals for form
  const { data: proposals } = useQuery({
    queryKey: ["proposals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select("id, title, client_id, status, tools")
        .order("title");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const { data, error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Project deleted successfully",
      });
      // Force invalidate and refetch projects
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.refetchQueries({ queryKey: ["projects"] });
      // Reset selected project if it was the deleted one
      setSelectedProject(null);
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      toast({
        title: "Error",
        description: "Failed to delete project: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Handle delete confirmation
  const handleDeleteProject = () => {
    if (selectedProject) {
      deleteProjectMutation.mutate(selectedProject.id);
    }
  };

  // Set first project as selected by default and update selectedProject when projects change
  React.useEffect(() => {
    if (projects && projects.length > 0) {
      if (!selectedProject) {
        setSelectedProject(projects[0]);
      } else {
        // Update the selectedProject with fresh data from projects array
        const updatedProject = projects.find(p => p.id === selectedProject.id);
        if (updatedProject) {
          setSelectedProject(updatedProject);
        }
      }
    }
  }, [projects]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load projects: {error.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Projects List */}
        <ProjectsList
          projects={projects || []}
          selectedProject={selectedProject}
          onSelectProject={setSelectedProject}
          onNewProject={() => setShowProjectForm(true)}
          isLoading={isLoading}
        />

        {/* Right Panel - Project Details */}
        <div className="flex-1 overflow-hidden">
          {selectedProject ? (
            <div className="h-full p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{selectedProject.name}</h2>
                  <p className="text-muted-foreground">{selectedProject.clients?.name}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditingInline(!isEditingInline)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {isEditingInline ? "View" : "Edit"}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="default" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Project</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{selectedProject?.name}"? This action cannot be undone and will also remove all associated data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDeleteProject}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={deleteProjectMutation.isPending}
                        >
                          {deleteProjectMutation.isPending ? "Deleting..." : "Delete Project"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              
              <ProjectDetailsTabs 
                project={selectedProject} 
                isEditingInline={isEditingInline}
                onEditingChange={setIsEditingInline}
              />
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-lg font-medium mb-2">Select a Project</h3>
                <p>Choose a project from the list to view its details and manage it.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project Form Dialog */}
      <ProjectForm
        open={showProjectForm}
        onOpenChange={(open) => {
          setShowProjectForm(open);
          if (!open) setEditingProject(null);
        }}
        project={editingProject}
        clients={clients || []}
        proposals={proposals || []}
      />
    </div>
  );
}